import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import UserBillingModal from '../components/UserBillingModal';
import { userAPI, planAPI, billingAPI, adminAPI } from '../services/api';

// Helper function to get full avatar URL
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return '';
  // If it's already a full URL (e.g., Google OAuth avatar), return as is
  if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
    return avatarPath;
  }
  // If it's a relative path, prepend API domain
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.getaizest.com/api';
  const baseUrl = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
  return `${baseUrl}${avatarPath}`;
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingUser, setBillingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [editForm, setEditForm] = useState({
    plan_id: '',
    start_date: '',
    expires_at: '',
    fallback_plan_id: '',
    fallback_expires_at: ''
  });
  const [editSuccessMessage, setEditSuccessMessage] = useState('');
  const [editErrorMessage, setEditErrorMessage] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [processingSubscriptions, setProcessingSubscriptions] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [showProcessConfirmDialog, setShowProcessConfirmDialog] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPlans();
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchUsers(true); // Silent refresh
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const fetchPlans = async () => {
    try {
      const response = await planAPI.getAll();
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const fetchUsers = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError('');
      const response = await userAPI.getAll();
      if (response.data.success) {
        setUsers(response.data.data.users || []);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (!silent) {
        setError('Failed to load users');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleManualRefresh = () => {
    fetchUsers();
  };

  const handleSuspend = async (userId, isActive) => {
    try {
      if (isActive) {
        await userAPI.suspend(userId);
      } else {
        await userAPI.unsuspend(userId);
      }
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Failed to update user status:', err);
      alert('Failed to update user status');
    }
  };

  const handleView = async (userId) => {
    try {
      const response = await userAPI.getOne(userId);
      if (response.data.success) {
        setUserDetails(response.data.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      alert('Failed to load user details');
    }
  };

  const handleViewBilling = (user) => {
    setBillingUser(user);
    setShowBillingModal(true);
  };

  const handleExportAllBilling = async () => {
    try {
      const response = await billingAPI.exportAllUsersReport();

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_users_billing_report_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export billing report:', err);
      alert('Failed to export billing report');
    }
  };

  const handleProcessExpiredSubscriptions = async () => {
    try {
      setProcessingSubscriptions(true);
      setProcessResult(null);
      setShowProcessConfirmDialog(false);
      const response = await adminAPI.processExpiredSubscriptions();

      if (response.data.success) {
        setProcessResult(response.data.data);
        // Refresh user list to show updated subscriptions
        fetchUsers();
      }
    } catch (err) {
      console.error('Failed to process expired subscriptions:', err);
      setProcessResult({
        message: 'Failed to process expired subscriptions',
        error: err.response?.data?.error?.message || err.message,
        isError: true
      });
    } finally {
      setProcessingSubscriptions(false);
    }
  };

  const handleEdit = async (user) => {
    setEditingUser(user);

    // Clear previous messages
    setEditSuccessMessage('');
    setEditErrorMessage('');

    // Get user's current subscription
    try {
      const response = await userAPI.getOne(user.id);
      if (response.data.success && response.data.data.subscriptions && response.data.data.subscriptions.length > 0) {
        const activeSub = response.data.data.subscriptions.find(sub => sub.status === 'active');
        if (activeSub) {
          setEditForm({
            plan_id: activeSub.plan_id,
            start_date: activeSub.current_period_start ? new Date(activeSub.current_period_start).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            expires_at: activeSub.current_period_end ? new Date(activeSub.current_period_end).toISOString().split('T')[0] : '',
            fallback_plan_id: activeSub.fallback_plan_id || '',
            fallback_expires_at: activeSub.fallback_period_end ? new Date(activeSub.fallback_period_end).toISOString().split('T')[0] : ''
          });
        } else {
          setEditForm({
            plan_id: '',
            start_date: new Date().toISOString().split('T')[0],
            expires_at: '',
            fallback_plan_id: '',
            fallback_expires_at: ''
          });
        }
      } else {
        // No subscription, default to today as start date
        setEditForm({
          plan_id: '',
          start_date: new Date().toISOString().split('T')[0],
          expires_at: '',
          fallback_plan_id: '',
          fallback_expires_at: ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch user subscription:', err);
      setEditForm({
        plan_id: '',
        start_date: new Date().toISOString().split('T')[0],
        expires_at: '',
        fallback_plan_id: '',
        fallback_expires_at: ''
      });
    }

    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    // Clear previous messages
    setEditSuccessMessage('');
    setEditErrorMessage('');

    if (!editForm.plan_id) {
      setEditErrorMessage('Please select a subscription plan');
      return;
    }

    if (!editForm.start_date) {
      setEditErrorMessage('Please select a start date');
      return;
    }

    // Validate dates if both are provided
    if (editForm.expires_at && editForm.start_date) {
      const startDate = new Date(editForm.start_date);
      const expiresDate = new Date(editForm.expires_at);
      if (expiresDate <= startDate) {
        setEditErrorMessage('Expiration date must be after start date');
        return;
      }
    }

    try {
      await userAPI.updateSubscription(editingUser.id, editForm);
      setEditSuccessMessage('Subscription updated successfully');

      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowEditModal(false);
        setEditingUser(null);
        setEditForm({ plan_id: '', start_date: '', expires_at: '', fallback_plan_id: '', fallback_expires_at: '' });
        setEditSuccessMessage('');
        fetchUsers(); // Refresh the list
      }, 2000);
    } catch (err) {
      console.error('Failed to update subscription:', err);
      setEditErrorMessage(err.response?.data?.error?.message || 'Failed to update subscription');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="User Management" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="User Management" />

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {processResult && (
            <div className={`${processResult.isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4 mb-4`}>
              <div className="flex items-start">
                {processResult.isError ? (
                  <svg className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                )}
                <div className="flex-1">
                  <h3 className={`${processResult.isError ? 'text-red-800' : 'text-green-800'} font-medium mb-2`}>{processResult.message}</h3>
                  {processResult.isError ? (
                    <p className="text-sm text-red-700">{processResult.error}</p>
                  ) : (
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Total processed: <strong>{processResult.processed}</strong></p>
                      <p>Reverted to fallback: <strong>{processResult.reverted}</strong></p>
                      <p>Downgraded to Free: <strong>{processResult.downgraded}</strong></p>
                      <p className="text-xs text-green-600 mt-2">{processResult.timestamp}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setProcessResult(null)}
                  className={`${processResult.isError ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} ml-4`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleManualRefresh}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center"
                    title="Refresh now"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                  <button
                    onClick={handleExportAllBilling}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export All Billing
                  </button>
                  <button
                    onClick={() => setShowProcessConfirmDialog(true)}
                    disabled={processingSubscriptions}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Process expired subscriptions and activate fallback plans"
                  >
                    {processingSubscriptions ? (
                      <>
                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Process Expired
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Auto-refresh controls */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Auto-refresh</span>
                  </label>

                  {autoRefresh && (
                    <select
                      value={refreshInterval}
                      onChange={(e) => setRefreshInterval(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={10000}>Every 10s</option>
                      <option value={30000}>Every 30s</option>
                      <option value={60000}>Every 1min</option>
                      <option value={120000}>Every 2min</option>
                      <option value={300000}>Every 5min</option>
                    </select>
                  )}
                </div>

                {lastUpdated && (
                  <div className="text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {user.avatar_url ? (
                              <img
                                src={getAvatarUrl(user.avatar_url)}
                                alt={user.full_name || user.email}
                                className="h-10 w-10 rounded-full mr-3"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                                <span className="text-gray-600 font-medium">
                                  {(user.full_name || user.email || '?')[0].toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">{user.full_name || 'N/A'}</span>
                                {user.pending_invoices > 0 && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                    </svg>
                                    {user.pending_invoices} Invoice{user.pending_invoices > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.oauth_provider ? (
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                              Google
                            </span>
                          ) : (
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Email
                              {user.is_email_verified && (
                                <svg className="w-4 h-4 ml-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                </svg>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.plan_name === 'Professional' ? 'bg-purple-100 text-purple-800' :
                              user.plan_name === 'Basic' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.plan_name || 'Free'}
                            </span>
                            {user.billing_period && (
                              <span className="text-xs text-gray-500 mt-1">
                                {user.billing_period === 'monthly' ? 'Monthly' :
                                 user.billing_period === 'quarterly' ? 'Quarterly' :
                                 user.billing_period === 'yearly' ? 'Yearly' : user.billing_period}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.current_period_end ? (
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900">
                                {new Date(user.current_period_end).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs mt-1 ${
                                new Date(user.current_period_end) < new Date()
                                  ? 'text-red-600'
                                  : new Date(user.current_period_end) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                              }`}>
                                {new Date(user.current_period_end) < new Date()
                                  ? 'Expired'
                                  : `${Math.ceil((new Date(user.current_period_end) - new Date()) / (1000 * 60 * 60 * 24))} days left`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleView(user.id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleViewBilling(user)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Billing
                          </button>
                          <button
                            onClick={() => handleSuspend(user.id, user.is_active)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            {user.is_active ? 'Suspend' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showModal && userDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">User Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* User Profile Section */}
              <div className="flex items-start space-x-6 mb-6 pb-6 border-b border-gray-200">
                {userDetails.user.avatar_url ? (
                  <img
                    src={getAvatarUrl(userDetails.user.avatar_url)}
                    alt={userDetails.user.full_name || userDetails.user.email}
                    className="h-24 w-24 rounded-full border-4 border-gray-100"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border-4 border-gray-100">
                    <span className="text-white text-3xl font-bold">
                      {(userDetails.user.full_name || userDetails.user.email || '?')[0].toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {userDetails.user.full_name || 'N/A'}
                  </h3>
                  <p className="text-gray-600 mb-3">{userDetails.user.email}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      userDetails.user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {userDetails.user.is_active ? 'Active' : 'Suspended'}
                    </span>
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {userDetails.user.roles?.join(', ') || 'user'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">User ID</p>
                    <p className="text-sm text-gray-900 font-mono">{userDetails.user.id}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Email Status</p>
                    <p className="text-sm text-gray-900">
                      {userDetails.user.is_email_verified ? (
                        <span className="flex items-center text-green-600">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          Verified
                        </span>
                      ) : (
                        <span className="text-red-600">Not Verified</span>
                      )}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Auth Method</p>
                    <p className="text-sm text-gray-900">{userDetails.user.oauth_provider || 'Email'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">OAuth ID</p>
                    <p className="text-sm text-gray-900 font-mono">{userDetails.user.oauth_id || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Joined Date</p>
                    <p className="text-sm text-gray-900">{new Date(userDetails.user.created_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Last Login</p>
                    <p className="text-sm text-gray-900">
                      {userDetails.user.last_login_at ? new Date(userDetails.user.last_login_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subscriptions */}
              {userDetails.subscriptions && userDetails.subscriptions.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Subscriptions
                  </h4>
                  <div className="space-y-3">
                    {userDetails.subscriptions.map((sub, index) => (
                      <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-100">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{sub.plan_name}</p>
                            <p className="text-sm text-gray-600">
                              Started: {sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : new Date(sub.created_at).toLocaleDateString()}
                              {sub.current_period_end && ` • Expires: ${new Date(sub.current_period_end).toLocaleDateString()}`}
                            </p>
                            {/* Show fallback plan for active subscriptions */}
                            {sub.status === 'active' && sub.fallback_plan_name && sub.fallback_period_end && (
                              <div className="mt-2 pt-2 border-t border-purple-200">
                                <p className="text-xs text-gray-500 mb-1">After expiry:</p>
                                <p className="text-sm text-purple-700 font-medium">
                                  {sub.fallback_plan_name} (until {new Date(sub.fallback_period_end).toLocaleDateString()})
                                </p>
                              </div>
                            )}
                          </div>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {sub.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Generations */}
              {userDetails.generations && userDetails.generations.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Content Generations
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {userDetails.generations.map((gen, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
                        <p className="text-sm text-gray-600 mb-1">{gen.content_type}</p>
                        <p className="text-2xl font-bold text-blue-600">{gen.count}</p>
                        <p className="text-xs text-gray-500">generations</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No additional data */}
              {(!userDetails.subscriptions || userDetails.subscriptions.length === 0) &&
               (!userDetails.generations || userDetails.generations.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p>No subscription or generation history yet</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subscription Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Edit Subscription</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* User Info */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  {editingUser.avatar_url ? (
                    <img
                      src={getAvatarUrl(editingUser.avatar_url)}
                      alt={editingUser.full_name || editingUser.email}
                      className="h-12 w-12 rounded-full"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                      <span className="text-white text-lg font-bold">
                        {(editingUser.full_name || editingUser.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{editingUser.full_name || 'N/A'}</p>
                    <p className="text-sm text-gray-600">{editingUser.email}</p>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {editSuccessMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-800 font-medium">{editSuccessMessage}</p>
                </div>
              )}

              {/* Error Message */}
              {editErrorMessage && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 font-medium">{editErrorMessage}</p>
                  </div>
                  <button
                    onClick={() => setEditErrorMessage('')}
                    className="text-red-600 hover:text-red-800 ml-4"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Subscription Plan */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Subscription Plan *
                </label>
                <select
                  value={editForm.plan_id}
                  onChange={(e) => setEditForm({ ...editForm, plan_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a plan</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={editForm.start_date}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  placeholder="YYYY-MM-DD"
                  lang="en-US"
                />
                <p className="mt-1 text-xs text-gray-500">Start date cannot be modified</p>
              </div>

              {/* Expiration Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Expiration Date (Optional)
                </label>
                <input
                  type="date"
                  value={editForm.expires_at}
                  onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="YYYY-MM-DD"
                  lang="en-US"
                />
                <p className="mt-1 text-xs text-gray-500">Leave empty for lifetime subscription</p>
              </div>

              {/* Fallback Section */}
              <div className={`border-t border-gray-200 pt-4 mt-4 ${!editForm.fallback_plan_id ? 'opacity-50' : ''}`}>
                <h3 className={`text-md font-semibold mb-4 flex items-center ${!editForm.fallback_plan_id ? 'text-gray-500' : 'text-gray-900'}`}>
                  <svg className={`w-5 h-5 mr-2 ${!editForm.fallback_plan_id ? 'text-gray-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  After Expiry (Fallback Plan)
                </h3>

                {/* Fallback Plan - Read Only Display */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${!editForm.fallback_plan_id ? 'text-gray-500' : 'text-gray-700'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    Fallback Subscription Plan
                  </label>
                  <input
                    type="text"
                    value={editForm.fallback_plan_id ? (plans.find(p => p.id === editForm.fallback_plan_id)?.name || 'Loading...') : 'No fallback plan'}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {editForm.fallback_plan_id ? 'Plan to activate after current subscription expires' : 'No fallback plan configured'}
                  </p>
                </div>

                {/* Fallback Expiration Date */}
                {editForm.fallback_plan_id && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Fallback Expiration Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={editForm.fallback_expires_at}
                      onChange={(e) => setEditForm({ ...editForm, fallback_expires_at: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="YYYY-MM-DD"
                      lang="en-US"
                    />
                    <p className="mt-1 text-xs text-gray-500">When the fallback subscription expires</p>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Subscription Update</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>This will update or create the user's active subscription</li>
                      <li>Previous active subscriptions will be marked as cancelled</li>
                      <li>Changes take effect immediately</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && billingUser && (
        <UserBillingModal
          user={billingUser}
          onClose={() => {
            setShowBillingModal(false);
            setBillingUser(null);
          }}
          onInvoiceUpdated={fetchUsers}
        />
      )}

      {/* Process Expired Subscriptions Confirmation Dialog */}
      {showProcessConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h2 className="text-xl font-semibold text-white">Confirm Processing</h2>
              <button
                onClick={() => setShowProcessConfirmDialog(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Process Expired Subscriptions
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to process expired subscriptions? This action will:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 mb-4">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Activate fallback plans for users with valid fallback periods</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Downgrade users to Free plan if no valid fallback exists</span>
                    </li>
                  </ul>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> This operation is safe to run multiple times and will only process subscriptions that have already expired.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200 rounded-b-lg">
              <button
                onClick={() => setShowProcessConfirmDialog(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessExpiredSubscriptions}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Yes, Process Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
